import React, { Fragment, useEffect, useState } from "react";
import { useAnnounceParcelTrackerContext } from "./announce-parcel-tracker-context";
import { Button, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { ScanFieldControlled } from "../../../../common/component/scan-field-controlled/scan-field-controlled";
import { Loop } from "@mui/icons-material";
import { t } from "i18next";
import { Parcel } from "../../../model/parcel";

interface IAnnounceParcelTrackerMultiStepFormProps {
    parcel: Parcel;
    remainingParcelCount: number;
    onSubmit: (carrier: string, tracker: string) => Promise<void>;
    onSkip: () => void;
    onCancel: () => void;
}

export const AnnounceParcelTrackerMultiStepForm = (props: IAnnounceParcelTrackerMultiStepFormProps) => {
    const { carrier, setCarrier } = useAnnounceParcelTrackerContext();
    const [carrierValue, setCarrierValue] = useState<string>(carrier);

    const handleCarrierSelected = (value: string) => {
        setCarrierValue(value);
        setCarrier(value);
    };

    const handleCarrierDeselected = () => {
        setCarrierValue("");
    };

    return !carrierValue ?
        <AnnounceParcelTrackerCarrierForm remainingParcelCount={props.remainingParcelCount}
                                          onSubmit={handleCarrierSelected} onCancel={props.onCancel} /> :
        <AnnounceParcelTrackerTrackerForm parcel={props.parcel}
                                          onCarrierDeselected={handleCarrierDeselected}
                                          onSubmit={async tracker => await props.onSubmit(carrierValue, tracker)}
                                          hasNext={props.remainingParcelCount > 1} onSkip={props.onSkip}
                                          onCancel={props.onCancel} />;
};

interface IAnnounceParcelTrackerCarrierFormProps {
    remainingParcelCount: number;
    onSubmit: (carrier: string) => void;
    onCancel: () => void;
}

const AnnounceParcelTrackerCarrierForm = (props: IAnnounceParcelTrackerCarrierFormProps) => {
    const { carrier } = useAnnounceParcelTrackerContext();
    const [carrierValue, setCarrierValue] = useState<string>(carrier);

    return <Fragment>
        <DialogTitle>
            {t("components.parcel.btmAssignTracker.form.carrier.title")}
        </DialogTitle>
        <DialogContent>
            <Typography className={"btm-tracker-announce-dialog__description"}>
                {t("components.parcel.btmAssignTracker.form.carrier.description", { parcelCount: props.remainingParcelCount })}
            </Typography>
            <FormControl variant={"outlined"} fullWidth>
                <FormLabel htmlFor={"btm-tracker-announcement-carrier"}>
                    {t("components.parcel.btmAssignTracker.form.carrier.label")}
                </FormLabel>
                <TextField id={"btm-tracker-announcement-carrier"} value={carrierValue} variant={"outlined"}
                           onChange={event => setCarrierValue(event.target.value ?? "")} />
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button variant={"outlined"} type={"submit"} key={"cancel-btn"} onClick={props.onCancel}>
                {t("components.parcel.btmAssignTracker.form.carrier.cancel")}
            </Button>
            <Button color={"secondary"} variant={"contained"} type={"submit"} key={"submit-btn"}
                    onClick={() => props.onSubmit(carrierValue)}>
                {t("components.parcel.btmAssignTracker.form.carrier.submit")}
            </Button>
        </DialogActions>
    </Fragment>;
};

interface IAnnounceParcelTrackerTrackerFormProps {
    parcel: Parcel;
    onCarrierDeselected: () => void;
    hasNext: boolean;
    onSubmit: (tracker: string) => void;
    onSkip: () => void;
    onCancel: () => void;
}

const AnnounceParcelTrackerTrackerForm = (props: IAnnounceParcelTrackerTrackerFormProps) => {
    const { carrier } = useAnnounceParcelTrackerContext();
    const [parcelTrackerValue, setParcelTrackerValue] = useState<string>("");

    useEffect(() => {
        setParcelTrackerValue("");
    }, [props.parcel]);

    return <Fragment>
        <DialogTitle>
            {t("components.parcel.btmAssignTracker.form.tracker.title", { parcel: props.parcel.id })}
        </DialogTitle>
        <DialogContent>
            <Typography className={"btm-tracker-announce-dialog__description"}>
                {t("components.parcel.btmAssignTracker.form.tracker.description", { carrier })}
            </Typography>
            <div className={"btm-tracker-announce-dialog__form"}>
                <FormControl variant={"filled"} fullWidth>
                    <FormLabel htmlFor={"btm-tracker-announcement-carrier"}>
                        {t("components.parcel.btmAssignTracker.form.tracker.label.carrier")}
                    </FormLabel>
                    <TextField id={"btm-tracker-announcement-carrier"}
                               value={carrier}
                               slotProps={{
                                   input: {
                                       readOnly: true,
                                       endAdornment: <InputAdornment position={"end"}>
                                           <IconButton onClick={props.onCarrierDeselected}>
                                               <Loop />
                                           </IconButton>
                                       </InputAdornment> }}}
                               variant={"outlined"}
                    />
                </FormControl>
                <ScanFieldControlled autoFocus withAutoSubmit
                                     textValue={parcelTrackerValue}
                                     onTextValueChange={value => setParcelTrackerValue(value)}
                                     onScan={props.onSubmit}>
                    <FormLabel>
                        {t("components.parcel.btmAssignTracker.form.tracker.label.tracker")}
                    </FormLabel>
                </ScanFieldControlled>
            </div>
        </DialogContent>
        <DialogActions>
            <Button variant={"outlined"} type={"submit"} key={"cancel-btn"}
                    onClick={props.onCancel}>
                {t("components.parcel.btmAssignTracker.form.tracker.cancel")}
            </Button>
            {props.hasNext && <Button variant={"outlined"} type={"submit"} key={"skip-btn"}
                    onClick={props.onSkip}>
                {t("components.parcel.btmAssignTracker.form.tracker.skip")}
            </Button>}
            <Button color={"secondary"}
                    variant={"contained"}
                    type={"submit"}
                    key={"submit-btn"}
                    onClick={() => props.onSubmit(parcelTrackerValue)}>
                {t("components.parcel.btmAssignTracker.form.tracker.submit", { carrier: carrier.substring(0, 8) })}
            </Button>
        </DialogActions>
    </Fragment>;
};
